import Component from '@glimmer/component';
import { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import { concat } from '@ember/helper';
import type { PlanExtension as PlanModel } from 'tio-data/data/resources/plan.ext';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import { divide } from 'tio-ui/math';
import { t } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import Button from 'tio-ui/components/button';
import { on } from '@ember/modifier';
import DescriptionList from 'tio-ui/components/description-list';

const number = Number;

export interface PlanSummarySignature {
  Args: {
    employee: EmployeeModel;
    model: PlanModel;
  };
  Element: HTMLElement;
}

export default class PlanSummary extends Component<PlanSummarySignature> {
  @service declare router: RouterService;
  @service declare store: StoreService;

  goToPlans = () => {
    this.router.transitionTo('authenticated.admin.plans', this.args.model.company.id);
  };

  <template>
    <Section @collapsed={{true}}>
      <:header>{{t "plans.plans"}}</:header>
      <:body>
        {{#if @employee.plan}}
          <DescriptionList
            @cols={{6}}
            @divided={{true}}
            class="border-b tio-border-default"
            as |dl|
          >
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_id"}}</dl.Term>
              <dl.Details>{{@employee.plan.id}}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_key"}}</dl.Term>
              <dl.Details>{{@employee.plan.companyKey}}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_status"}}</dl.Term>
              <dl.Details>{{t "common.placeholder"}}</dl.Details>
            </dl.Group>
            {{! status attribute in employee model is commented out }}
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_name"}}</dl.Term>
              <dl.Details>{{@employee.plan.name}}</dl.Details>
            </dl.Group>
            {{#let
              (safeFormatDate @employee.plan.createdAt month="long" day="2-digit" year="numeric")
              as |createdAt|
            }}
              <dl.Group>
                <dl.Term>{{t "plans.summary.plan_creation_date"}}</dl.Term>
                <dl.Details>{{createdAt}}</dl.Details>
              </dl.Group>
            {{/let}}
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_waiting_period"}}</dl.Term>
              <dl.Details>{{@employee.plan.waitingPeriod}}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.plan_type"}}</dl.Term>
              <dl.Details>{{t "common.placeholder"}}</dl.Details>
            </dl.Group>
            {{! there is no plan type attribute }}
          </DescriptionList>
          <p class="p-2 leading-loose tio-fg-muted font-semibold uppercase">
            {{t "contributions.default"}}
          </p>
          <DescriptionList
            @cols={{5}}
            @divided={{true}}
            class="border-b tio-border-default"
            as |dl|
          >
            <dl.Group>
              <dl.Term>{{t "plans.summary.pay_parent_plus"}}</dl.Term>
              <dl.Details>{{if
                  @employee.plan.checkParentPlusLoans
                  (t "common.no")
                  (t "common.yes")
                }}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.min_monthly_payment_required"}}</dl.Term>
              <dl.Details>{{if
                  @employee.plan.isMinMonthly
                  (t "common.yes")
                  (t "common.no")
                }}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.lifetime_max_contribution_per_employer"}}</dl.Term>
              <dl.Details>{{concat "$" @employee.plan.lifetimeLimit}}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.annual_max_contribution_per_employee"}}</dl.Term>
              <dl.Details>{{concat "$" @employee.plan.annualLimit}}</dl.Details>
            </dl.Group>
            <dl.Group>
              <dl.Term>{{t "plans.summary.cadence"}}</dl.Term>
              <dl.Details>{{@employee.plan.cadence}}</dl.Details>
            </dl.Group>
          </DescriptionList>
          <p class="p-2 leading-loose tio-fg-muted font-semibold uppercase">
            {{t "plan_tiers"}}
          </p>
          <DescriptionList @cols={{2}} as |dl|>
            {{#each @employee.plan.tiers as |tier|}}
              <dl.Group>
                <dl.Term>{{concat (t "plans.from_cycle") " " tier.from-cycle}}</dl.Term>
                <dl.Details>{{concat
                    "$"
                    (divide (number tier.amount) 100)
                    " "
                    tier.per
                  }}</dl.Details>
              </dl.Group>
            {{/each}}
          </DescriptionList>
        {{else}}
          <p class="p-2 leading-loose tio-fg-muted">
            {{t "plans.no_plan_attached"}}
          </p>
        {{/if}}
        <hr class="tio-border-default" />
        <Button {{on "click" this.goToPlans}} class="mt-4" @intent="primary" @appearance="outlined">
          {{t "plans.view_all"}}
        </Button>
      </:body>
    </Section>
  </template>
}
